/**
 * Full-screen overlays: menu, categories, search.
 *
 * One controller for all three so the choreography, focus handling and
 * scroll locking are identical. Opening any of them tells the rest of the
 * page (via sc:overlay) so the hero pauses its autoplay.
 */
/**
 * Resolve the shared motion singleton.
 *
 * Do NOT `import` sc-motion-core.js from here. Section scripts are served from
 * the same CDN directory, so a relative specifier resolves to the
 * UNVERSIONED asset URL while the layout loads the VERSIONED one. Two URLs
 * means two module instances — two Lenis controllers, two cleanup sets, and
 * a race over window.SCMotion.
 */
const whenMotion = (fn) => {
  if (window.SCMotion?.ScrollTrigger) { fn(window.SCMotion); return; }
  document.addEventListener('sc:motion:ready', () => fn(window.SCMotion), { once: true });
};
const Motion = new Proxy({}, { get: (_, k) => window.SCMotion?.[k] });

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

let active = null;
let lastTrigger = null;

function reducedMotionRequested() {
  return Boolean(Motion.reduced || document.documentElement.classList.contains('reduce-site-motion'));
}

function destinationFocusTarget(target) {
  if (!target) return null;

  /* `#scr-categories` is a layout marker intentionally hidden from assistive
   * technology. Never focus it: move to the labelled section heading instead. */
  const container = target.getAttribute('aria-hidden') === 'true'
    ? target.closest('section') || target.parentElement
    : target;
  if (!container) return null;

  const labelledBy = container.getAttribute?.('aria-labelledby')?.trim().split(/\s+/)[0];
  return (labelledBy && document.getElementById(labelledBy))
    || container.querySelector?.('h1, h2, h3, h4, h5, h6, [role="heading"]')
    || container;
}

function focusDestination(target) {
  const focusTarget = destinationFocusTarget(target);
  if (!focusTarget || focusTarget.getAttribute?.('aria-hidden') === 'true') return;

  const temporaryTabindex = !focusTarget.hasAttribute('tabindex');
  const removeTemporaryTabindex = () => {
    if (temporaryTabindex && focusTarget.getAttribute('tabindex') === '-1') {
      focusTarget.removeAttribute('tabindex');
    }
  };

  if (temporaryTabindex) focusTarget.setAttribute('tabindex', '-1');
  focusTarget.addEventListener('blur', removeTemporaryTabindex, { once: true });
  focusTarget.focus({ preventScroll: true });
  if (document.activeElement !== focusTarget) removeTemporaryTabindex();
}

function moveToDestination(target) {
  target.scrollIntoView({ block: 'start', behavior: reducedMotionRequested() ? 'auto' : 'smooth' });
  focusDestination(target);
}

/* The approved menu links to stable editorial anchors. All referenced
 * sections already expose an id except the wardrobe, whose source remains
 * untouched because its four-piece launch configuration is deliberate. */
function ensureEditorialAnchors() {
  const wardrobe = document.querySelector('[data-scr-wardrobe]');
  if (wardrobe && !wardrobe.id) wardrobe.id = 'scr-wardrobe';
  return wardrobe;
}

const editorialHashes = new Set(['#scr-categories', '#scr-wardrobe', '#scr-journal', '#scr-about', '#scr-social']);
ensureEditorialAnchors();
if (editorialHashes.has(location.hash)) {
  const destination = document.querySelector(location.hash);
  if (destination) requestAnimationFrame(() => moveToDestination(destination));
}
document.addEventListener('shopify:section:load', () => {
  ensureEditorialAnchors();
  if (!editorialHashes.has(location.hash)) return;
  const destination = document.querySelector(location.hash);
  if (destination) requestAnimationFrame(() => moveToDestination(destination));
});

function trapFocus(panel, e) {
  if (e.key !== 'Tab') return;
  const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

function open(name, trigger) {
  const panel = document.querySelector(`[data-sc-overlay="${name}"]`);
  if (!panel || active === name) return;

  if (active) close({ silent: true });        // only one at a time
  active = name;
  lastTrigger = trigger || document.activeElement;

  panel.hidden = false;
  // Force a style flush so the browser records the closed state, then flip
  // the class in the same task. rAF proved unreliable here — the callback
  // could be coalesced away and the class never landed.
  void panel.offsetHeight;
  panel.classList.add('is-open');
  panel.setAttribute('aria-hidden', 'false');

  Motion.pause();                              // locks scroll, remembers position
  document.dispatchEvent(new CustomEvent('sc:overlay', { detail: { open: true, name } }));

  const focusTarget = panel.querySelector('[data-sc-autofocus]')
    || panel.querySelector(FOCUSABLE);
  focusTarget?.focus({ preventScroll: true });

  panel._onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    trapFocus(panel, e);
  };
  panel.addEventListener('keydown', panel._onKey);
}

function close({ silent = false, restoreFocus = true } = {}) {
  if (!active) return;
  const panel = document.querySelector(`[data-sc-overlay="${active}"]`);
  active = null;

  if (panel) {
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.removeEventListener('keydown', panel._onKey);
    // Wait for the exit transition before removing from the a11y tree.
    const done = (ev) => {
      if (ev && ev.target !== panel) return;          // ignore child transitions
      if (panel.classList.contains('is-open')) return; // reopened meanwhile
      panel.hidden = true;
      panel.removeEventListener('transitionend', done);
    };
    if (reducedMotionRequested()) done();
    else panel.addEventListener('transitionend', done);
  }

  if (!silent) {
    Motion.resume();                           // restores the saved scroll position
    document.dispatchEvent(new CustomEvent('sc:overlay', { detail: { open: false } }));
    if (restoreFocus) lastTrigger?.focus({ preventScroll: true });
    lastTrigger = null;
  }
}

/* --- delegated wiring, survives section reloads ------------------- */
document.addEventListener('click', (e) => {
  const opener = e.target.closest?.('[data-sc-open]');
  if (opener) { e.preventDefault(); open(opener.dataset.scOpen, opener); return; }
  const closer = e.target.closest?.('[data-sc-close]');
  if (closer) { e.preventDefault(); close(); return; }
  const homeAnchor = e.target.closest?.('[data-sc-home-anchor]');
  if (homeAnchor) {
    const destination = new URL(homeAnchor.href, location.href);
    const samePage = destination.origin === location.origin && destination.pathname === location.pathname;
    const target = samePage && destination.hash ? document.querySelector(destination.hash) : null;
    if (target) {
      e.preventDefault();
      close({ restoreFocus: false });
      history.pushState(null, '', destination.hash);
      requestAnimationFrame(() => moveToDestination(target));
      return;
    }
  }
  // Clicking the scrim closes; clicking the panel does not.
  if (active && e.target.matches?.('[data-sc-overlay].is-open')) close();
});

window.SCOverlay = { open, close, get active() { return active; } };
